import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import {
  IndianRupee, TrendingUp, TrendingDown, AlertTriangle, Receipt, Wallet,
  PlusCircle, Trash2, Check, ChevronRight, DollarSign, HandCoins, ArrowDownCircle, BellRing,
} from "lucide-react";

const TABS = [
  { key: "overview",    label: "Overview",     icon: TrendingUp },
  { key: "collections", label: "Collections",  icon: HandCoins },
  { key: "outstanding", label: "Outstanding",  icon: AlertTriangle },
  { key: "expenses",    label: "Expenses",     icon: ArrowDownCircle },
  { key: "salaries",    label: "Staff Salaries", icon: Wallet },
];

const EXPENSE_CATEGORIES = ["utilities", "maintenance", "supplies", "equipment", "marketing", "misc"];

function todayISO() { return new Date().toISOString().slice(0, 10); }
function monthOf(d) { return d.slice(0, 7); }
function firstOfMonth() { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); }

function KPI({ label, value, hint, icon: Icon, tone = "blue" }) {
  const toneMap = {
    blue: "brand-blue border-brand-blue/40",
    orange: "brand-orange border-brand-orange/40",
    green: "text-emerald-400 border-emerald-500/40",
    red: "text-red-400 border-red-500/40",
    white: "text-white border-white/20",
  };
  return (
    <div className={`border ${toneMap[tone].split(" ")[1]} bg-[#131A2B] p-5`} data-testid={`kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-white/40">{label}</div>
        <Icon size={14} className="text-white/40" />
      </div>
      <div className={`font-display text-3xl font-black mt-1 ${toneMap[tone].split(" ")[0]}`}>{value}</div>
      {hint && <div className="text-[10px] font-mono text-white/40 mt-1">{hint}</div>}
    </div>
  );
}

function Overview({ summary, from, to, setFrom, setTo, refresh }) {
  if (!summary) return <div className="text-white/40 py-8">Loading…</div>;
  return (
    <div>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="text-xs font-mono uppercase tracking-widest text-white/50">Period</div>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          data-testid="acc-from"
          className="bg-[#0B1120] border border-white/20 py-2 px-3 text-sm" />
        <div className="text-white/40">to</div>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
          data-testid="acc-to"
          className="bg-[#0B1120] border border-white/20 py-2 px-3 text-sm" />
        <button onClick={refresh} data-testid="acc-refresh"
          className="text-xs font-mono uppercase tracking-widest bg-brand-blue text-white px-4 py-2 hover:glow-blue">
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
        <KPI label="Fees Collected" value={formatINR(summary.fees_collected)} hint={`${summary.invoice_count} invoices`} icon={Receipt} tone="blue" />
        <KPI label="Bookings Revenue" value={formatINR(summary.bookings_revenue)} hint={`${summary.booking_count} bookings`} icon={IndianRupee} tone="blue" />
        <KPI label="Gross Revenue" value={formatINR(summary.gross_revenue)} hint="Fees + bookings" icon={TrendingUp} tone="green" />
        <KPI label="Outstanding" value={formatINR(summary.outstanding)} hint={`${summary.outstanding_count} unpaid`} icon={AlertTriangle} tone="orange" />
        <KPI label="Expenses" value={formatINR(summary.expenses)} icon={ArrowDownCircle} tone="red" />
        <KPI label="Salaries Paid" value={formatINR(summary.salaries)} icon={Wallet} tone="red" />
        <KPI label="Net Income" value={formatINR(summary.net_income)} hint="Revenue - Expenses - Salaries" icon={TrendingUp} tone={summary.net_income >= 0 ? "green" : "red"} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-white/12 bg-[#131A2B] p-5" data-testid="acc-by-service">
          <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-4">// Revenue by service</div>
          {Object.entries(summary.by_service || {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border-b border-white/10 py-2 last:border-none">
              <span className="text-sm capitalize">{k.replace(/-/g, " ")}</span>
              <span className="brand-blue font-display">{formatINR(v)}</span>
            </div>
          ))}
          {Object.keys(summary.by_service || {}).length === 0 && <div className="text-white/40 text-sm">No revenue in period</div>}
        </div>
        <div className="border border-white/12 bg-[#131A2B] p-5" data-testid="acc-by-category">
          <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-4">// Expenses by category</div>
          {Object.entries(summary.by_expense_category || {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border-b border-white/10 py-2 last:border-none">
              <span className="text-sm capitalize">{k}</span>
              <span className="text-red-400 font-display">{formatINR(v)}</span>
            </div>
          ))}
          {Object.keys(summary.by_expense_category || {}).length === 0 && <div className="text-white/40 text-sm">No expenses logged</div>}
        </div>
      </div>
    </div>
  );
}

function Collections({ from, to }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (from) params.set("from_date", from);
    if (to) params.set("to_date", to);
    api.get(`/admin/accounting/collections?${params.toString()}`)
      .then((r) => setRows(r.data))
      .finally(() => setLoading(false));
  }, [from, to]);
  const totals = useMemo(() => rows.reduce((s, r) => s + (r.amount || 0), 0), [rows]);
  return (
    <div data-testid="acc-collections">
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-xs uppercase tracking-widest brand-orange">// Payments received ({rows.length})</div>
        <div className="brand-blue font-display text-2xl">{formatINR(totals)}</div>
      </div>
      {loading && <div className="text-white/40 py-8">Loading…</div>}
      <div className="border border-white/12 bg-[#131A2B]">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/12 text-[10px] font-mono uppercase tracking-widest text-white/40">
          <div className="col-span-2">Date</div>
          <div className="col-span-2">Reference</div>
          <div className="col-span-2">Party</div>
          <div className="col-span-3">Description</div>
          <div className="col-span-1">Service</div>
          <div className="col-span-1">Method</div>
          <div className="col-span-1 text-right">Amount</div>
        </div>
        {rows.map((r) => (
          <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/8 text-xs hover:bg-white/4">
            <div className="col-span-2 font-mono text-white/60">{(r.date || "").slice(0, 10)}</div>
            <div className="col-span-2 font-mono text-white/60">{r.reference}</div>
            <div className="col-span-2 truncate">{r.party}</div>
            <div className="col-span-3 text-white/70 truncate">{r.description}</div>
            <div className="col-span-1 text-[10px] uppercase font-mono brand-orange truncate">{r.kind}</div>
            <div className="col-span-1 text-[10px] uppercase font-mono text-white/50">{r.method}</div>
            <div className="col-span-1 text-right brand-blue font-display">{formatINR(r.amount)}</div>
          </div>
        ))}
        {rows.length === 0 && !loading && <div className="p-6 text-center text-white/40 text-sm">No collections in period</div>}
      </div>
    </div>
  );
}

function Outstanding({ reload }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingId, setMarkingId] = useState(null);

  const load = () => {
    setLoading(true);
    return api.get("/admin/accounting/outstanding").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const markPaid = async (row) => {
    if (!window.confirm(`Mark ${row.invoice_number} paid?`)) return;
    setMarkingId(row.id);
    try {
      await api.post("/admin/accounting/mark-invoice-paid", {
        invoice_id: row.id,
        description: row.description,
        amount: row.amount,
        method: "cash",
        received_on: todayISO(),
      });
      toast.success("Marked paid");
      await load();
      reload && reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setMarkingId(null);
    }
  };

  const total = rows.reduce((s, r) => s + r.amount, 0);

  const pushRemindersAll = async () => {
    if (!window.confirm("Send push notification to every parent with outstanding invoices?")) return;
    try {
      const { data } = await api.post("/admin/push/send-overdue-reminders");
      toast.success(`Push sent to ${data.parents_notified} parent${data.parents_notified === 1 ? "" : "s"} (${data.pushes_sent} devices)`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="acc-outstanding">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="font-mono text-xs uppercase tracking-widest brand-orange">// Unpaid invoices ({rows.length})</div>
        <div className="flex items-center gap-3">
          <button
            onClick={pushRemindersAll}
            disabled={rows.length === 0}
            data-testid="push-reminders-all"
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-3 py-2 hover:bg-brand-blue hover:text-white disabled:opacity-40"
          >
            <BellRing size={12} /> Push reminders to all
          </button>
          <div className="brand-orange font-display text-2xl">{formatINR(total)}</div>
        </div>
      </div>
      {loading && <div className="text-white/40 py-8">Loading…</div>}
      <div className="border border-white/12 bg-[#131A2B]">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/12 text-[10px] font-mono uppercase tracking-widest text-white/40">
          <div className="col-span-2">Invoice #</div>
          <div className="col-span-2">Parent</div>
          <div className="col-span-2">Ward</div>
          <div className="col-span-3">Description</div>
          <div className="col-span-1">Due</div>
          <div className="col-span-1 text-right">Amount</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        {rows.map((r) => (
          <div key={r.id} className={`grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/8 text-xs hover:bg-white/4 ${r.status === "overdue" ? "bg-red-500/5" : ""}`} data-testid={`outstanding-row-${r.id}`}>
            <div className="col-span-2 font-mono text-white/60">{r.invoice_number}</div>
            <div className="col-span-2 truncate">{r.parent_name}</div>
            <div className="col-span-2 truncate">{r.ward_name}</div>
            <div className="col-span-3 text-white/70 truncate">{r.description}</div>
            <div className="col-span-1 font-mono">
              <span className={r.status === "overdue" ? "text-red-400" : "text-white/60"}>{r.due_date}</span>
            </div>
            <div className="col-span-1 text-right brand-orange font-display">{formatINR(r.amount)}</div>
            <div className="col-span-1 text-right">
              <button
                onClick={() => markPaid(r)}
                disabled={markingId === r.id}
                data-testid={`mark-paid-${r.id}`}
                className="text-[10px] font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-2 py-1 hover:bg-brand-blue hover:text-white disabled:opacity-50"
              >
                <Check size={10} className="inline mr-1" /> Paid
              </button>
            </div>
          </div>
        ))}
        {rows.length === 0 && !loading && <div className="p-6 text-center text-white/40 text-sm">Nothing outstanding — great job!</div>}
      </div>
    </div>
  );
}

function Expenses() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ category: "utilities", description: "", amount: "", paid_to: "", paid_on: todayISO(), method: "cash", notes: "" });

  const load = () => api.get("/admin/accounting/expenses").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/accounting/expenses", {
        ...form, amount: parseFloat(form.amount) || 0,
      });
      toast.success("Expense recorded");
      setForm({ category: "utilities", description: "", amount: "", paid_to: "", paid_on: todayISO(), method: "cash", notes: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/admin/accounting/expenses/${id}`);
    load();
  };

  const total = items.reduce((s, r) => s + (r.amount || 0), 0);
  return (
    <div data-testid="acc-expenses">
      <div className="border border-white/12 bg-[#131A2B] p-5 mb-6">
        <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-4">// Log expense</div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            data-testid="exp-category"
            className="col-span-2 bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm">
            {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Description" required data-testid="exp-description"
            className="col-span-2 bg-transparent border border-white/20 py-2.5 px-3 text-sm" />
          <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="₹ Amount" required data-testid="exp-amount"
            className="col-span-1 bg-transparent border border-white/20 py-2.5 px-3 text-sm" />
          <input type="date" value={form.paid_on} onChange={(e) => setForm({ ...form, paid_on: e.target.value })}
            required data-testid="exp-date"
            className="col-span-1 bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm" />
          <input value={form.paid_to} onChange={(e) => setForm({ ...form, paid_to: e.target.value })}
            placeholder="Paid to (optional)"
            className="col-span-3 bg-transparent border border-white/20 py-2.5 px-3 text-sm" />
          <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}
            className="col-span-1 bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm">
            <option>cash</option><option>upi</option><option>card</option><option>netbanking</option><option>cheque</option>
          </select>
          <button type="submit" data-testid="exp-submit"
            className="col-span-2 bg-brand-blue text-white py-2.5 font-bold text-xs uppercase tracking-widest hover:glow-blue flex items-center justify-center gap-2">
            <PlusCircle size={14} /> Add expense
          </button>
        </form>
      </div>

      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-xs uppercase tracking-widest brand-orange">// All expenses ({items.length})</div>
        <div className="text-red-400 font-display text-2xl">{formatINR(total)}</div>
      </div>
      <div className="border border-white/12 bg-[#131A2B]">
        {items.map((e) => (
          <div key={e.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/8 text-xs hover:bg-white/4">
            <div className="col-span-1 font-mono text-white/60">{e.paid_on}</div>
            <div className="col-span-2 uppercase text-[10px] brand-orange font-mono">{e.category}</div>
            <div className="col-span-4 text-white/80 truncate">{e.description}</div>
            <div className="col-span-2 text-white/50 truncate">{e.paid_to || "—"}</div>
            <div className="col-span-1 text-[10px] font-mono text-white/50 uppercase">{e.method}</div>
            <div className="col-span-1 text-right text-red-400 font-display">{formatINR(e.amount)}</div>
            <div className="col-span-1 text-right">
              <button onClick={() => del(e.id)} className="text-white/40 hover:text-red-400"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-6 text-center text-white/40 text-sm">No expenses recorded</div>}
      </div>
    </div>
  );
}

function Salaries() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ staff_name: "", role: "", month: new Date().toISOString().slice(0,7), amount: "", notes: "" });
  const [filterMonth, setFilterMonth] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (filterMonth) params.set("month", filterMonth);
    if (filterStatus) params.set("status", filterStatus);
    return api.get(`/admin/accounting/salaries?${params.toString()}`).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); }, [filterMonth, filterStatus]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/accounting/salaries", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success("Salary entry created");
      setForm({ staff_name: "", role: "", month: new Date().toISOString().slice(0,7), amount: "", notes: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const pay = async (id) => {
    await api.post(`/admin/accounting/salaries/${id}/pay`);
    toast.success("Marked paid");
    load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this salary entry?")) return;
    await api.delete(`/admin/accounting/salaries/${id}`);
    load();
  };

  const totalPending = items.filter((i) => i.status === "pending").reduce((s, r) => s + r.amount, 0);
  const totalPaid = items.filter((i) => i.status === "paid").reduce((s, r) => s + r.amount, 0);

  return (
    <div data-testid="acc-salaries">
      <div className="grid grid-cols-2 gap-4 mb-6">
        <KPI label="Pending" value={formatINR(totalPending)} icon={AlertTriangle} tone="orange" />
        <KPI label="Paid" value={formatINR(totalPaid)} icon={Check} tone="green" />
      </div>

      <div className="border border-white/12 bg-[#131A2B] p-5 mb-6">
        <div className="font-mono text-xs uppercase tracking-widest brand-orange mb-4">// New salary entry</div>
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <input value={form.staff_name} onChange={(e) => setForm({ ...form, staff_name: e.target.value })}
            placeholder="Staff name" required data-testid="sal-name"
            className="col-span-2 bg-transparent border border-white/20 py-2.5 px-3 text-sm" />
          <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
            placeholder="Role (e.g. Head Coach)" data-testid="sal-role"
            className="col-span-2 bg-transparent border border-white/20 py-2.5 px-3 text-sm" />
          <input type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })}
            required data-testid="sal-month"
            className="col-span-1 bg-[#0B1120] border border-white/20 py-2.5 px-3 text-sm" />
          <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="₹ Amount" required data-testid="sal-amount"
            className="col-span-1 bg-transparent border border-white/20 py-2.5 px-3 text-sm" />
          <button type="submit" data-testid="sal-submit"
            className="col-span-6 bg-brand-blue text-white py-2.5 font-bold text-xs uppercase tracking-widest hover:glow-blue">
            Add salary entry
          </button>
        </form>
      </div>

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)}
          placeholder="Filter month" className="bg-[#0B1120] border border-white/20 py-2 px-3 text-sm" />
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-[#0B1120] border border-white/20 py-2 px-3 text-sm">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
        </select>
        {(filterMonth || filterStatus) && (
          <button onClick={() => { setFilterMonth(""); setFilterStatus(""); }} className="text-xs text-white/40 hover:text-white">Clear</button>
        )}
      </div>

      <div className="border border-white/12 bg-[#131A2B]">
        {items.map((s) => (
          <div key={s.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/8 text-xs items-center" data-testid={`sal-row-${s.id}`}>
            <div className="col-span-3 font-bold">{s.staff_name}</div>
            <div className="col-span-3 text-white/60">{s.role || "—"}</div>
            <div className="col-span-1 font-mono text-white/60">{s.month}</div>
            <div className="col-span-1 text-right brand-blue font-display">{formatINR(s.amount)}</div>
            <div className="col-span-2 text-[10px] font-mono uppercase">
              {s.status === "paid"
                ? <span className="text-emerald-400">Paid {s.paid_on || ""}</span>
                : <span className="brand-orange">Pending</span>}
            </div>
            <div className="col-span-2 text-right flex items-center gap-2 justify-end">
              {s.status !== "paid" && (
                <button onClick={() => pay(s.id)} data-testid={`sal-pay-${s.id}`}
                  className="text-[10px] font-mono uppercase tracking-widest border border-brand-blue text-brand-blue px-2 py-1 hover:bg-brand-blue hover:text-white">
                  Mark paid
                </button>
              )}
              <button onClick={() => del(s.id)} className="text-white/40 hover:text-red-400"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-6 text-center text-white/40 text-sm">No salary entries</div>}
      </div>
    </div>
  );
}

export default function AdminAccounting() {
  const [tab, setTab] = useState("overview");
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(todayISO());
  const [summary, setSummary] = useState(null);

  const refresh = async () => {
    const params = new URLSearchParams();
    if (from) params.set("from_date", from);
    if (to) params.set("to_date", to);
    try {
      const { data } = await api.get(`/admin/accounting/summary?${params.toString()}`);
      setSummary(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { refresh(); }, []); // eslint-disable-line

  return (
    <div data-testid="admin-accounting">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Accounting · Finance</div>
      <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-2">Ledger.</h1>
      <p className="text-sm text-white/60 mb-8 max-w-2xl">
        Track collections, outstanding fees, expenses and staff salaries. Mark invoices paid when
        parents settle offline (cash / bank transfer).
      </p>

      <div className="flex flex-wrap gap-1 mb-6 border-b border-white/12">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`acc-tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-3 text-xs font-mono uppercase tracking-widest border-b-2 ${
              tab === t.key
                ? "border-brand-orange text-white"
                : "border-transparent text-white/50 hover:text-white"
            }`}
          >
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" &&    <Overview summary={summary} from={from} to={to} setFrom={setFrom} setTo={setTo} refresh={refresh} />}
      {tab === "collections" && <Collections from={from} to={to} />}
      {tab === "outstanding" && <Outstanding reload={refresh} />}
      {tab === "expenses" &&    <Expenses />}
      {tab === "salaries" &&    <Salaries />}
    </div>
  );
}
